import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALLERGY_CRITICALITIES,
  ALLERGY_REACTION_SEVERITIES,
  formatEatDate,
  formatEatTime,
  VITAL_SIGNS,
} from '@wodi/domain';
import type {
  AllergyReviewRecordView,
  AllergyReviewRequest,
  ProblemView,
  WorkspaceView,
} from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  RadioGroup,
  SearchPicker,
  Section,
  Tag,
  TextArea,
  VITAL_FLAG_WORDS,
  type SearchPickerStatus,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  allergyHistoryQuery,
  problemEnteredInError,
  reviewAllergy,
  searchAllergens,
  updateProblem,
} from '../api/consultation.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import styles from './consultation.module.css';

// The patient summary (plan §7.1): vitals today, allergies with review, problems,
// previous visits and the data-sharing consent state. Allergies are always stated: a
// list, "No known allergies" or "Allergies not recorded" (CLAUDE.md).

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** The one-line "Vitals today" strip (DESIGN.md §6.3). */
export function VitalsToday({ view }: { view: WorkspaceView }) {
  const set = view.vitals;
  if (set === null) {
    return (
      <p className={styles.vitals}>
        <span className={styles.vitalsLabel}>Vitals today</span>
        <span>Not taken</span>
      </p>
    );
  }
  const current = set.observations.filter((o) => o.status === 'final');
  return (
    <p className={styles.vitals} aria-label="Vitals today">
      <span className={styles.vitalsLabel}>
        {`Vitals today ${formatEatTime(new Date(set.effectiveAt))}`}
      </span>
      {current.map((o) => {
        const flag = o.interpretation === 'normal' ? null : VITAL_FLAG_WORDS[o.interpretation];
        const critical =
          o.interpretation === 'critical_low' || o.interpretation === 'critical_high';
        const abnormal = flag !== null && o.interpretation !== 'no_range';
        return (
          <span
            key={o.id}
            className={styles.vital}
            data-abnormal={abnormal || undefined}
            data-critical={critical || undefined}
          >
            {`${VITAL_SIGNS[o.key].label} ${o.value} ${o.unit.display}${abnormal ? ` (${flag})` : ''}`}
          </span>
        );
      })}
      {set.bmi === null ? null : <span className={styles.vital}>{`BMI ${set.bmi}`}</span>}
    </p>
  );
}

const VERIFICATION_LABELS: Record<string, string> = {
  unconfirmed: 'Reported, not confirmed',
  confirmed: 'Confirmed',
  refuted: 'Ruled out',
  'entered-in-error': 'Entered in error',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Current',
  inactive: 'Inactive',
  resolved: 'Resolved',
};

function AllergenPicker({
  onSelect,
}: {
  onSelect: (allergen: {
    kind: 'substance' | 'allergen_group';
    system: string;
    code: string;
    display: string;
  }) => void;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const search = useQuery({
    queryKey: ['allergen-search', trimmed],
    queryFn: () => searchAllergens(trimmed),
    enabled: online && trimmed.length >= 2,
    staleTime: 30_000,
    retry: false,
  });
  let status: SearchPickerStatus;
  if (trimmed.length < 2) status = 'idle';
  else if (!online) status = 'offline';
  else if (search.isFetching || search.isPending) status = 'searching';
  else if (search.isError) status = 'error';
  else status = 'ready';
  return (
    <SearchPicker
      label="Code to a substance or allergen group (optional)"
      description="From your organisation's list, so prescribing can check it."
      inputValue={text}
      onInputChange={setText}
      status={status}
      items={search.data?.items ?? []}
      itemsQuery={trimmed}
      getKey={(a) => `${a.kind}:${a.system}:${a.code}`}
      getTextValue={(a) => a.display}
      renderItem={(a) => (
        <span>
          {a.display}
          <span className={styles.itemMeta}>
            {a.kind === 'substance' ? ' · Substance' : ' · Allergen group'}
          </span>
        </span>
      )}
      onSelect={onSelect}
      emptyMessage="Nothing matches. The pharmacist adds substances in Settings."
      errorMessage={errorText(search.error, 'Search did not work. Try again.')}
      offlineMessage="Searching needs a connection to Wodi."
    />
  );
}

function ReviewDialog({
  view,
  allergy,
  onClose,
}: {
  view: WorkspaceView;
  allergy: AllergyReviewRecordView;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [verification, setVerification] = useState(allergy.verificationStatus);
  const [status, setStatus] = useState(allergy.clinicalStatus);
  const [severity, setSeverity] = useState<string | null>(allergy.severity);
  const [criticality, setCriticality] = useState<string | null>(allergy.criticality);
  const [allergen, setAllergen] = useState<{
    kind: 'substance' | 'allergen_group';
    system: string;
    code: string;
    display: string;
  } | null>(null);
  const [reason, setReason] = useState('');
  const body: AllergyReviewRequest = {
    visitId: view.visit.id,
    reason: reason.trim(),
    ...(verification === allergy.verificationStatus
      ? {}
      : { verificationStatus: verification as 'unconfirmed' | 'confirmed' | 'refuted' }),
    ...(status === allergy.clinicalStatus
      ? {}
      : { clinicalStatus: status as 'active' | 'inactive' | 'resolved' }),
    ...(severity === allergy.severity
      ? {}
      : { reactionSeverity: severity as 'mild' | 'moderate' | 'severe' | null }),
    ...(criticality === allergy.criticality
      ? {}
      : { criticality: criticality as 'low' | 'high' | 'unable-to-assess' | null }),
    ...(allergen === null
      ? {}
      : { allergen: { kind: allergen.kind, system: allergen.system, code: allergen.code } }),
  };
  const changed = Object.keys(body).length > 2;
  const save = useMutation({
    mutationFn: () => reviewAllergy(allergy.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'workspace'] });
      await queryClient.invalidateQueries({ queryKey: ['patients', view.visit.patientId] });
      await queryClient.invalidateQueries({ queryKey: ['allergies', allergy.id] });
      toasts.show({ title: 'Allergy reviewed' });
      onClose();
    },
  });
  return (
    <Dialog
      title={`Review allergy: ${allergy.substance}`}
      isOpen
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <RadioGroup
        label="Is it true?"
        value={verification}
        onChange={setVerification}
        options={(['unconfirmed', 'confirmed', 'refuted'] as const).map((v) => ({
          id: v,
          label: VERIFICATION_LABELS[v] ?? v,
        }))}
      />
      <RadioGroup
        label="Status"
        orientation="horizontal"
        value={status}
        onChange={setStatus}
        options={(['active', 'inactive', 'resolved'] as const).map((v) => ({
          id: v,
          label: STATUS_LABELS[v] ?? v,
        }))}
      />
      <RadioGroup
        label="Reaction severity"
        orientation="horizontal"
        value={severity}
        onChange={setSeverity}
        options={ALLERGY_REACTION_SEVERITIES.map((v) => ({
          id: v,
          label: v.charAt(0).toUpperCase() + v.slice(1),
        }))}
      />
      <RadioGroup
        label="Criticality"
        orientation="horizontal"
        value={criticality}
        onChange={setCriticality}
        options={ALLERGY_CRITICALITIES.map((v) => ({
          id: v,
          label: v === 'unable-to-assess' ? 'Unable to assess' : v === 'high' ? 'High' : 'Low',
        }))}
      />
      {allergy.allergen === null ? null : (
        <p className={styles.help}>{`Coded to ${allergy.allergen.display}.`}</p>
      )}
      {allergen === null ? (
        <AllergenPicker onSelect={setAllergen} />
      ) : (
        <p className={styles.itemTitle}>
          {`Code to: ${allergen.display} `}
          <Button
            dense
            variant="quiet"
            onPress={() => {
              setAllergen(null);
            }}
          >
            Change
          </Button>
        </p>
      )}
      <TextArea label="Reason" value={reason} onChange={setReason} rows={2} />
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'The allergy could not be saved.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!changed || reason.trim().length < 5}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Save review
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AllergyHistoryDialog({ allergyId, onClose }: { allergyId: string; onClose: () => void }) {
  const history = useQuery(allergyHistoryQuery(allergyId));
  return (
    <Dialog
      title="Allergy history"
      isOpen
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      {history.isPending ? <LoadingBlock label="Loading the history" rows={3} /> : null}
      {history.isError ? (
        <Notice tone="error">{errorText(history.error, 'The history could not be loaded.')}</Notice>
      ) : null}
      <ul className={styles.list}>
        {(history.data?.versions ?? []).map((v) => (
          <li key={v.version} className={styles.item}>
            <span className={styles.itemMain}>
              <span className={styles.itemTitle}>
                {`${VERIFICATION_LABELS[v.verificationStatus] ?? v.verificationStatus}, ${(STATUS_LABELS[v.clinicalStatus] ?? v.clinicalStatus).toLowerCase()}`}
                {v.allergen === null ? '' : `, coded to ${v.allergen}`}
              </span>
              <span className={styles.itemMeta}>
                {`${formatEatDate(new Date(v.changedAt))} ${formatEatTime(new Date(v.changedAt))} by ${v.changedBy.displayName}${v.reason === null ? '' : `: ${v.reason}`}`}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <DialogActions>
        <Button onPress={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function AllergiesSection({ view, mayVerify }: { view: WorkspaceView; mayVerify: boolean }) {
  const [reviewing, setReviewing] = useState<AllergyReviewRecordView | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const shown = view.allergyRecords.filter((a) => a.verificationStatus !== 'entered-in-error');
  return (
    <Section title="Allergies">
      {view.allergies.state === 'list' ? null : (
        <p className={styles.itemTitle}>
          {view.allergies.state === 'no_known_allergies'
            ? 'No known allergies'
            : 'Allergies not recorded'}
        </p>
      )}
      <ul className={styles.list}>
        {shown.map((a) => {
          const current = a.clinicalStatus === 'active' && a.verificationStatus !== 'refuted';
          return (
            <li key={a.id} className={styles.item} data-inactive={!current || undefined}>
              <span className={styles.itemMain}>
                <span className={styles.itemTitle}>
                  {current ? <Tag state="danger">{a.substance}</Tag> : a.substance}
                </span>
                <span className={styles.itemMeta}>
                  {[
                    a.reaction,
                    a.severity === null ? 'Severity not recorded' : `Severity ${a.severity}`,
                    VERIFICATION_LABELS[a.verificationStatus] ?? a.verificationStatus,
                    current ? null : STATUS_LABELS[a.clinicalStatus],
                    a.allergen === null ? 'Not coded' : `Coded: ${a.allergen.display}`,
                  ]
                    .filter((x) => x !== null && x !== undefined)
                    .join(' · ')}
                </span>
              </span>
              <span className={styles.actions}>
                {mayVerify && view.visit.status === 'open' ? (
                  <Button
                    dense
                    variant="quiet"
                    onPress={() => {
                      setReviewing(a);
                    }}
                  >
                    Review
                  </Button>
                ) : null}
                <Button
                  dense
                  variant="quiet"
                  onPress={() => {
                    setHistory(a.id);
                  }}
                >
                  History
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
      {reviewing === null ? null : (
        <ReviewDialog
          view={view}
          allergy={reviewing}
          onClose={() => {
            setReviewing(null);
          }}
        />
      )}
      {history === null ? null : (
        <AllergyHistoryDialog
          allergyId={history}
          onClose={() => {
            setHistory(null);
          }}
        />
      )}
    </Section>
  );
}

function ProblemsSection({ view, mayWrite }: { view: WorkspaceView; mayWrite: boolean }) {
  const queryClient = useQueryClient();
  const [resolving, setResolving] = useState<ProblemView | null>(null);
  const [erroring, setErroring] = useState<ProblemView | null>(null);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'workspace'] });
  const resolve = useMutation({
    mutationFn: (reason: string) =>
      updateProblem(resolving?.id ?? '', {
        visitId: view.visit.id,
        expectedVersion: resolving?.version ?? 0,
        clinicalStatus: 'resolved',
        reason,
      }),
    onSuccess: async () => {
      setResolving(null);
      await refresh();
      toasts.show({ title: 'Problem resolved' });
    },
  });
  const markError = useMutation({
    mutationFn: (reason: string) =>
      problemEnteredInError(erroring?.id ?? '', { visitId: view.visit.id, reason }),
    onSuccess: async () => {
      setErroring(null);
      await refresh();
      toasts.show({ title: 'Marked as entered in error' });
    },
  });
  const shown = view.problems.filter((p) => p.status === 'active');
  const writable = mayWrite && view.visit.status === 'open';
  return (
    <Section title="Problem list">
      {shown.length === 0 ? (
        <p className={styles.help}>No problems recorded. Add one from a diagnosis.</p>
      ) : null}
      <ul className={styles.list}>
        {shown.map((p) => (
          <li
            key={p.id}
            className={styles.item}
            data-inactive={p.clinicalStatus !== 'active' || undefined}
          >
            <span className={styles.itemMain}>
              <span className={styles.itemTitle}>
                {p.coding === null ? null : (
                  <span className={`wodi-id ${styles.code}`}>{p.coding.code}</span>
                )}
                {p.coding?.display ?? p.clinicianText}
              </span>
              <span className={styles.itemMeta}>
                {[
                  STATUS_LABELS[p.clinicalStatus],
                  p.onsetDate === null ? null : `Since ${p.onsetDate}`,
                ]
                  .filter((x) => x !== null && x !== undefined)
                  .join(' · ')}
              </span>
            </span>
            {writable ? (
              <span className={styles.actions}>
                {p.clinicalStatus === 'resolved' ? null : (
                  <Button
                    dense
                    variant="quiet"
                    onPress={() => {
                      setResolving(p);
                    }}
                  >
                    Resolve
                  </Button>
                )}
                <Button
                  dense
                  variant="quiet"
                  onPress={() => {
                    setErroring(p);
                  }}
                >
                  Entered in error
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      <ReasonDialog
        title="Resolve the problem"
        isOpen={resolving !== null}
        onOpenChange={(o) => {
          if (!o) setResolving(null);
        }}
        confirmLabel="Resolve problem"
        pendingLabel="Saving…"
        isPending={resolve.isPending}
        error={resolve.error === null ? null : errorText(resolve.error, 'It could not be saved.')}
        onConfirm={(reason) => {
          resolve.mutate(reason);
        }}
      />
      <ReasonDialog
        title="Mark the problem as entered in error"
        isOpen={erroring !== null}
        onOpenChange={(o) => {
          if (!o) setErroring(null);
        }}
        confirmLabel="Mark as entered in error"
        pendingLabel="Marking…"
        isPending={markError.isPending}
        error={
          markError.error === null ? null : errorText(markError.error, 'It could not be marked.')
        }
        onConfirm={(reason) => {
          markError.mutate(reason);
        }}
      >
        <p>Nothing is deleted: the problem stays in the history, marked with your reason.</p>
      </ReasonDialog>
    </Section>
  );
}

const CONSENT_WORDS = {
  granted: 'agreed',
  refused: 'refused',
  withdrawn: 'withdrawn',
  not_asked: 'not asked',
} as const;

const CONSENT_DECISION_WORDS = {
  granted: 'Agreed',
  refused: 'Refused',
  withdrawn: 'Withdrawn',
} as const;

export function SummaryPanel({
  view,
  mayWrite,
  mayVerify,
}: {
  view: WorkspaceView;
  mayWrite: boolean;
  mayVerify: boolean;
}) {
  return (
    <div className={styles.summary}>
      <AllergiesSection view={view} mayVerify={mayVerify} />
      <ProblemsSection view={view} mayWrite={mayWrite} />
      <Section title="Previous visits">
        {view.previousVisits.length === 0 ? (
          <p className={styles.help}>No earlier visits.</p>
        ) : (
          <ul className={styles.list}>
            {view.previousVisits.map((v) => (
              <li key={v.id} className={styles.item}>
                <span className={styles.itemMain}>
                  <span className={styles.itemTitle}>{formatEatDate(new Date(v.startedAt))}</span>
                  <span className={styles.itemMeta}>
                    {[
                      v.diagnoses.length === 0 ? 'No diagnosis recorded' : v.diagnoses.join(', '),
                      v.clinicians.join(', '),
                    ]
                      .filter((x) => x !== '')
                      .join(' · ')}
                  </span>
                </span>
                <span className={`wodi-id ${styles.itemMeta}`}>{v.visitNumber}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Data sharing">
        <p className={styles.help}>
          {`Data sharing: ${CONSENT_WORDS[view.consent.state]}${view.consent.recordedAt === null ? '' : ` ${formatEatDate(new Date(view.consent.recordedAt))}`}. Reception records changes.`}
        </p>
        {view.consent.history[0]?.reason == null ? null : (
          <p className={styles.help}>{`Reason given: ${view.consent.history[0].reason}`}</p>
        )}
        {view.consent.history.length > 1 ? (
          <ul className={styles.list} aria-label="Earlier data-sharing decisions">
            {view.consent.history.slice(1).map((c) => (
              <li key={`${c.recordedAt}-${c.decision}`} className={styles.item}>
                <span className={styles.itemMain}>
                  <span className={styles.itemTitle}>{CONSENT_DECISION_WORDS[c.decision]}</span>
                  {c.reason === null ? null : <span className={styles.itemMeta}>{c.reason}</span>}
                </span>
                <span className={styles.itemMeta}>{formatEatDate(new Date(c.recordedAt))}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </Section>
    </div>
  );
}
